import { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '../../stores/auth-store';
import { dashboardApi } from '../../services/api-services';
import type { SummaryDashboard } from '../../types/api';
import { KpiCard } from '../../components/ui/KpiCard';
import { SystemStatusPanel } from '../../components/ui/SystemStatusPanel';

// ─────────────────────────────────────────────────────────────
// Dashboard — Operations Deck (Variation A)
// Real KPIs: Companies / Locations / Wells / Users (via dashboardApi).
// Sparklines use seeded placeholder data until backend time-series exists.
// ─────────────────────────────────────────────────────────────

// Deterministic pseudo-random — keeps sparklines stable across renders.
function seeded(seed: number) {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

// Build a 30-point series that trends gently toward `target`.
function genSeries(target: number, seed: number): number[] {
  if (!target || target < 1) return [];
  const rng = seeded(seed);
  const start = Math.max(1, target * 0.7);
  return Array.from({ length: 30 }, (_, i) => {
    const t = i / 29;
    const base = start + (target - start) * t;
    return base + (rng() - 0.5) * (target * 0.08);
  });
}

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = user?.role === 'SuperAdmin';

  const [summary, setSummary] = useState<SummaryDashboard | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = isSuperAdmin
          ? await dashboardApi.getSummary()
          : await dashboardApi.getSummaryByCompany(Number(user?.companyId ?? 0));
        if (res.entity) setSummary(res.entity);
      } catch {
        /* swallow — KPIs will show 0 */
      }
      setLoading(false);
    };
    load();
  }, [isSuperAdmin, user?.companyId]);

  const kpis = useMemo(() => {
    const companies = summary?.noofCompanies ?? 0;
    const sites = summary?.noofSites ?? 0;
    const wells = summary?.noofWells ?? 0;
    const users = summary?.noofUsers ?? 0;

    return [
      ...(isSuperAdmin
        ? [
            {
              label: 'Registered companies',
              value: companies,
              unit: companies === 1 ? 'company' : 'companies',
              sub: 'Tenant directory',
              accent: 'var(--color-accent)',
              data: genSeries(companies, 1),
              href: '/dashboard/companies',
            },
          ]
        : []),
      {
        label: 'Active locations',
        value: sites,
        unit: sites === 1 ? 'location' : 'locations',
        sub: 'Field sites under management',
        accent: 'var(--color-teal)',
        data: genSeries(sites, 2),
        href: '/dashboard/sites',
      },
      {
        label: 'Total wells',
        value: wells,
        unit: wells === 1 ? 'well' : 'wells',
        sub: 'Operational + drilling',
        accent: 'var(--color-warn)',
        data: genSeries(wells, 3),
        href: '/dashboard/wells',
      },
      {
        label: 'Active users',
        value: users,
        unit: users === 1 ? 'user' : 'users',
        sub: 'Across all roles',
        accent: '#7c6ba8',
        data: genSeries(users, 4),
        href: '/dashboard/users',
      },
    ];
  }, [summary, isSuperAdmin]);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Greeting strip */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              color: 'var(--color-text-dim)',
              fontWeight: 500,
            }}
          >
            Administration
          </div>
          <div
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 28,
              fontWeight: 500,
              letterSpacing: -0.6,
              color: 'var(--color-text)',
              marginTop: 4,
            }}
          >
            {greeting}, {user?.username ?? 'operator'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--color-text-mid)', marginTop: 4 }}>
            Here's what's happening across your tenancy today.
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: 'rgba(26,143,90,0.08)',
            color: 'var(--color-good)',
            padding: '6px 12px',
            borderRadius: 999,
            fontFamily: 'var(--font-mono)',
            fontSize: 10.5,
            letterSpacing: 0.8,
            fontWeight: 600,
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: 3, background: 'var(--color-good)' }} />
          ALL SYSTEMS OPERATIONAL
        </div>
      </div>

      {/* KPI grid — 1col mobile, 2col tablet, 3-4 col desktop */}
      <div
        className={`grid gap-4 sm:grid-cols-2 ${
          isSuperAdmin ? 'lg:grid-cols-4' : 'lg:grid-cols-3'
        }`}
      >
        {kpis.map((k) => (
          <KpiCard
            key={k.label}
            label={k.label}
            value={loading ? '—' : k.value.toLocaleString()}
            unit={k.unit}
            sub={k.sub}
            accent={k.accent}
            data={loading ? undefined : k.data}
            href={k.href}
            loading={loading}
          />
        ))}
      </div>

      {/* Lower row — System status + Quick info */}
      <div className="grid gap-4 lg:grid-cols-2">
        <SystemStatusPanel />

        <div
          style={{
            background: 'var(--color-panel)',
            border: '1px solid var(--color-border)',
            borderRadius: 10,
            padding: 22,
          }}
        >
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10.5,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              fontWeight: 500,
              color: 'var(--color-text-dim)',
            }}
          >
            Session
          </div>
          <div
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 22,
              fontWeight: 500,
              letterSpacing: -0.4,
              color: 'var(--color-text)',
              marginTop: 2,
              marginBottom: 14,
            }}
          >
            Quick overview
          </div>
          <p
            style={{
              fontSize: 13.5,
              color: 'var(--color-text-mid)',
              lineHeight: 1.6,
              marginBottom: 16,
            }}
          >
            Use the sidebar to manage companies, users, wells, and other resources. Click any KPI card
            above to jump to that section.
          </p>
          <dl style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: 0 }}>
            <InfoRow label="Role" value={user?.role ?? '—'} />
            {user?.companyId && Number(user.companyId) > 0 && (
              <InfoRow label="Company ID" value={String(user.companyId)} />
            )}
            <InfoRow label="User ID" value={String(user?.userId ?? '—')} />
          </dl>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        paddingBottom: 8,
        borderBottom: '1px solid var(--color-border)',
      }}
    >
      <dt
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 10.5,
          letterSpacing: 1.2,
          textTransform: 'uppercase',
          color: 'var(--color-text-dim)',
          width: 110,
        }}
      >
        {label}
      </dt>
      <dd
        style={{
          fontSize: 13.5,
          color: 'var(--color-text)',
          fontWeight: 500,
          margin: 0,
        }}
      >
        {value}
      </dd>
    </div>
  );
}
